import BackupCodesView from '@/components/TwoFactor/BackupCodesView';
import { RECOMMENDED_METHOD } from '@/components/TwoFactor/MethodChoice';
import MfaPill from '@/components/TwoFactor/MfaPill';
import EmailCodeSetup from '@/components/TwoFactor/EmailCodeSetup';
import TotpSetup from '@/components/TwoFactor/TotpSetup';
import { twoFactorDialogOpenAtom, userAtom } from '@/global-store';
import type { MfaSetupConfirmation } from '@/http/queries/useConfirmMfaSetup';
import { useFetchMfaStatus } from '@/http/queries/useFetchMfaStatus';
import { useRegenerateBackupCodes } from '@/http/queries/useRegenerateBackupCodes';
import { useProveIdentity } from '@/http/queries/useProveIdentity';
import { useRemoveMfaMethod } from '@/http/queries/useRemoveMfaMethod';
import { T } from '@/i18n/T';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { cn } from '@helpers/cn';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Typography,
  useLocaleFormat,
} from '@eye-able/ui';
import type { MfaMethodType, MfaStatusResponse } from '@eye-able/types';
import { useAtom, useAtomValue } from 'jotai';
import { KeyRound, Mail, Shield, ShieldCheck, Smartphone } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import MfaProofForm from './MfaProofForm';

const ALL_METHODS: MfaMethodType[] = ['totp', 'email'];
const icons = { totp: Smartphone, email: Mail } as const;

type View =
  | { name: 'overview' }
  | { name: 'proof'; type: MfaMethodType }
  | { name: 'setup'; type: MfaMethodType; challenge: string }
  | { name: 'remove'; type: MfaMethodType }
  | { name: 'regenerate' }
  | { name: 'backupCodes'; codes: string[]; justEnabled: boolean };

/** The button that opened the dialog; focus returns there, since the popover it sat in is gone. */
export const twoFactorOpener: { current: HTMLElement | null } = {
  current: null,
};

/** Where the user turns their second factors on and off. */
function TwoFactorDialog() {
  const { t } = useInlineTranslation();
  const [open, onOpenChange] = useAtom(twoFactorDialogOpenAtom);
  const user = useAtomValue(userAtom);
  const status = useFetchMfaStatus({ enabled: open });
  const [view, setView] = useState<View>({ name: 'overview' });
  const removeMethod = useRemoveMfaMethod();
  const regenerate = useRegenerateBackupCodes();
  const prove = useProveIdentity();

  const toOverview = () => setView({ name: 'overview' });
  // Reset on close, so codes do not linger and reopening starts at the overview.
  const close = (next: boolean) => onOpenChange(next);
  // After the exit animation: focus goes to the profile button, since the
  // popover that held the 2FA entry is gone, and the next open starts fresh.
  const afterClose = (event: Event) => {
    event.preventDefault();
    twoFactorOpener.current?.focus();
    toOverview();
  };
  const onConfirmed = (confirmation: MfaSetupConfirmation) => {
    if (confirmation.backupCodes) {
      setView({
        name: 'backupCodes',
        codes: confirmation.backupCodes,
        justEnabled: true,
      });
    } else {
      toOverview();
    }
  };

  // Codes on screen must not vanish on a stray click outside.
  const persistent = view.name !== 'overview';

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        maxWidth='sm'
        className='eye:w-full'
        persistent={persistent}
        onCloseAutoFocus={afterClose}
      >
        <DialogTitle>{title(view, t)}</DialogTitle>
        {description(view, status.data) ? (
          <DialogDescription>
            <T
              k={description(view, status.data)!}
              options={
                view.name === 'remove'
                  ? { method: t(`mfaMethod_${view.type}`) }
                  : undefined
              }
            />
          </DialogDescription>
        ) : null}
        <div className='eye:mt-2xs'>
          {view.name === 'overview' ? (
            <Overview
              status={status.data}
              error={status.error}
              email={user?.email ?? ''}
              onSetup={(type) => setView({ name: 'proof', type })}
              onRemove={(type) => setView({ name: 'remove', type })}
              onRegenerate={() => setView({ name: 'regenerate' })}
              onClose={() => close(false)}
            />
          ) : null}
          {view.name === 'proof' ? (
            <MfaProofForm
              methods={status.data?.methods.map((method) => method.type) ?? []}
              email={user?.email ?? ''}
              askCode={!!status.data?.enabled}
              submitLabel={t('continue')}
              onSubmit={async (proof) => {
                const { challenge } = await prove.mutateAsync(proof);
                setView({ name: 'setup', type: view.type, challenge });
              }}
              onCancel={toOverview}
            />
          ) : null}
          {view.name === 'setup' && view.type === 'totp' ? (
            <TotpSetup
              challenge={view.challenge}
              onConfirmed={onConfirmed}
              onBack={toOverview}
              errorOverrides={profileErrorWording}
            />
          ) : null}
          {view.name === 'setup' && view.type === 'email' ? (
            <EmailCodeSetup
              challenge={view.challenge}
              email={user?.email ?? ''}
              onConfirmed={onConfirmed}
              onBack={toOverview}
              errorOverrides={profileErrorWording}
            />
          ) : null}
          {view.name === 'remove' ? (
            <MfaProofForm
              methods={status.data?.methods.map((method) => method.type) ?? []}
              email={user?.email ?? ''}
              submitLabel={t('mfaRemoveMethod')}
              onSubmit={async ({ password, code = '' }) => {
                await removeMethod.mutateAsync({
                  password,
                  code,
                  type: view.type,
                });
                toOverview();
              }}
              onCancel={toOverview}
            />
          ) : null}
          {view.name === 'regenerate' ? (
            <MfaProofForm
              methods={status.data?.methods.map((method) => method.type) ?? []}
              email={user?.email ?? ''}
              submitLabel={t('mfaRegenerateBackupCodes')}
              onSubmit={async ({ password, code = '' }) => {
                const { backupCodes } = await regenerate.mutateAsync({
                  password,
                  code,
                });
                if (backupCodes) {
                  setView({
                    name: 'backupCodes',
                    codes: backupCodes,
                    justEnabled: false,
                  });
                }
              }}
              onCancel={toOverview}
            />
          ) : null}
          {view.name === 'backupCodes' ? (
            <div className='eye:flex eye:flex-col eye:gap-16'>
              <BackupCodesView
                codes={view.codes}
                justEnabled={view.justEnabled}
              />
              <DialogActions>
                <Button type='button' variant='outline' onClick={toOverview}>
                  {t('mfaBackupCodesSaved')}
                </Button>
              </DialogActions>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** In the profile a dead challenge means the setup expired; the login is fine. */
const profileErrorWording = {
  mfaChallengeExpired: 'mfaSetupExpired',
} as const;

/** The line under the title that says what the step asks for. */
function description(
  view: View,
  status: MfaStatusResponse | undefined,
): string | null {
  switch (view.name) {
    case 'overview':
      return 'mfaIntro';
    case 'proof':
      if (!status?.enabled) return 'mfaProofPasswordHint';
      return status.methods.some((method) => method.type === 'totp')
        ? 'mfaProofPasswordAndAppCodeHint'
        : 'mfaProofPasswordAndMailCodeHint';
    case 'remove':
      return 'mfaRemoveMethodHint';
    case 'regenerate':
      return 'mfaRegenerateBackupCodesHint';
    default:
      return null;
  }
}

/** The design names each step; the overview alone carries the feature name. */
function title(view: View, t: ReturnType<typeof useInlineTranslation>['t']) {
  switch (view.name) {
    case 'proof':
    case 'setup':
      return t('mfaAddMethodNamed', { method: t(`mfaMethod_${view.type}`) });
    case 'remove':
      return t('mfaRemoveMethodNamed', {
        method: t(`mfaMethod_${view.type}`),
      });
    case 'regenerate':
      return t('mfaRegenerateBackupCodes');
    case 'backupCodes':
      return t('mfaBackupCodesOnceTitle');
    default:
      return t('twoFactorAuthentication');
  }
}

interface OverviewProps {
  status: MfaStatusResponse | undefined;
  /** The status request failed; nothing can be shown or changed. */
  error: unknown;
  /** Shown under the mail method, so the user sees where codes go. */
  email: string;
  onSetup: (type: MfaMethodType) => void;
  onRemove: (type: MfaMethodType) => void;
  onRegenerate: () => void;
  onClose: () => void;
}

function Overview({
  status,
  error,
  email,
  onSetup,
  onRemove,
  onRegenerate,
  onClose,
}: OverviewProps) {
  const { t } = useInlineTranslation();
  const { formatDate } = useLocaleFormat();
  const methodsHeadingId = useId();
  const recoveryHeadingId = useId();

  if (error) {
    // Not a code error, whatever the status says: the request itself failed.
    return (
      <div className='eye:flex eye:flex-col eye:gap-16'>
        <Alert variant='error' role='alert'>
          {t('errorTryAgain')}
        </Alert>
        <DialogActions>
          <Button type='button' variant='outline' onClick={onClose}>
            {t('close')}
          </Button>
        </DialogActions>
      </div>
    );
  }
  if (!status) {
    return (
      <div className='eye:flex eye:justify-center eye:py-m'>
        <CircularProgress aria-label={t('loading')} />
      </div>
    );
  }
  // The API refuses to remove the last method under the organisation's rule.
  const removable = !(
    status.organisationRequiresMfa && status.methods.length === 1
  );

  const verifiedAt = (type: MfaMethodType) =>
    status.methods.find((method) => method.type === type)?.verifiedAt;
  const detail = (type: MfaMethodType) => {
    const since = verifiedAt(type);
    if (!since) return t('mfaNotSetUp');
    if (type === 'email') return email;
    return t('mfaSetUpOn', { date: formatDate(new Date(since)) });
  };

  return (
    <div className='eye:flex eye:flex-col eye:gap-16'>
      <StatusCard enabled={status.enabled} />

      <div className='eye:flex eye:flex-col eye:gap-4'>
        <SectionHeading id={methodsHeadingId}>
          <T k='mfaMethods' />
        </SectionHeading>
        <ul
          aria-labelledby={methodsHeadingId}
          className='eye:flex eye:flex-col'
        >
          {ALL_METHODS.map((type) => {
            const Icon = icons[type];
            const name = t(`mfaMethod_${type}`);
            return (
              <Row
                key={type}
                icon={<Icon aria-hidden />}
                title={name}
                badge={
                  type === RECOMMENDED_METHOD && !verifiedAt(type) ? (
                    <MfaPill tone='recommended'>{t('mfaRecommended')}</MfaPill>
                  ) : null
                }
                detail={detail(type)}
              >
                {verifiedAt(type) ? (
                  <>
                    <MfaPill tone='on'>{t('mfaOn')}</MfaPill>
                    {removable ? (
                      <Button
                        type='button'
                        variant='ghost'
                        size='sm'
                        className='eye:md:text-14'
                        aria-label={t('mfaRemoveMethodNamed', { method: name })}
                        onClick={() => onRemove(type)}
                      >
                        {t('mfaRemoveMethod')}
                      </Button>
                    ) : null}
                  </>
                ) : (
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    className='eye:md:text-14'
                    aria-label={t('mfaAddMethodNamed', { method: name })}
                    onClick={() => onSetup(type)}
                  >
                    {t('add')}
                  </Button>
                )}
              </Row>
            );
          })}
        </ul>
      </div>

      {status.enabled ? (
        <div className='eye:flex eye:flex-col eye:gap-4'>
          <SectionHeading id={recoveryHeadingId}>
            <T k='mfaRecovery' />
          </SectionHeading>
          <ul
            aria-labelledby={recoveryHeadingId}
            className='eye:flex eye:flex-col'
          >
            <Row
              icon={<KeyRound aria-hidden />}
              title={t('mfaBackupCodes')}
              detail={t('mfaBackupCodesRemaining', {
                count: status.backupCodesRemaining,
              })}
            >
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='eye:md:text-14'
                onClick={onRegenerate}
              >
                {t('mfaRegenerateBackupCodes')}
              </Button>
            </Row>
          </ul>
        </div>
      ) : null}

      <DialogActions>
        <Button type='button' variant='outline' onClick={onClose}>
          {t('close')}
        </Button>
      </DialogActions>
    </div>
  );
}

/** Green when every login needs a second factor, plain while only the password guards the account. */
function StatusCard({ enabled }: { enabled: boolean }) {
  const { t } = useInlineTranslation();
  const Icon = enabled ? ShieldCheck : Shield;
  return (
    <div
      className={cn(
        'eye:flex eye:items-center eye:gap-12 eye:rounded-md eye:border eye:px-16 eye:py-12',
        enabled
          ? 'eye:border-transparent eye:bg-green-25'
          : 'eye:border-grey-20',
      )}
    >
      <Icon aria-hidden className='eye:shrink-0' />
      <div className='eye:min-w-0 eye:flex-1'>
        <div className='eye:text-14 eye:leading-[20px] eye:font-semibold'>
          <T k={enabled ? 'mfaStatusOn' : 'mfaStatusOff'} />
        </div>
        <div className='eye:text-12 eye:leading-[18px] eye:text-grey-120'>
          <T k={enabled ? 'mfaStatusOnHint' : 'mfaStatusOffHint'} />
        </div>
      </div>
      {enabled ? null : <MfaPill tone='off'>{t('mfaOff')}</MfaPill>}
    </div>
  );
}

function SectionHeading({ id, children }: { id: string; children: ReactNode }) {
  return (
    <Typography tag='h3' variant='body-m-highlight' id={id}>
      {children}
    </Typography>
  );
}

interface RowProps {
  icon: ReactNode;
  title: string;
  /** Next to the title, e.g. a recommendation. */
  badge?: ReactNode;
  detail: string;
  /** The pill and the action on the right. */
  children: ReactNode;
}

/** One method or the backup codes: icon, name, a line of detail, actions. */
function Row({ icon, title, badge, detail, children }: RowProps) {
  return (
    <li className='eye:flex eye:items-center eye:gap-12 eye:border-b eye:border-grey-20 eye:py-12 eye:last:border-b-0'>
      <span className='eye:shrink-0'>{icon}</span>
      <div className='eye:min-w-0 eye:flex-1'>
        <div className='eye:flex eye:flex-wrap eye:items-center eye:gap-8 eye:text-14 eye:leading-[20px] eye:font-semibold'>
          {title}
          {badge}
        </div>
        <div className='eye:text-12 eye:leading-[18px] eye:text-grey-120'>
          {detail}
        </div>
      </div>
      {children}
    </li>
  );
}

export default TwoFactorDialog;
